import { apiClient } from '../../../../api/axios';
import type { ApiResponse, PaginatedResponse } from '../common/types';
import type {
    ReviewerEvaluationAssignedSubmission,
    GetMemberAssignedSubmissionsForReviewerEvaluationPayload,
} from '../reviewerEvaluation/types';
import type {
    CategoryMemberDropdownItem,
    AssignMembersForPrimaFaciePayload,
    PrimaFacieAssignedSubmission,
    GetMemberAssignedSubmissionsForPrimaFaciePayload,
    PrimaFacieAssignmentDetail,
    CreateUpdatePrimaFacieReviewPayload,
    PrimaFacieProcessItem,
    GetSubmissionUnderPrimaFacieProcessPayload,
    PrimaFacieSuggestedReviewerItem,
    SubmitFinalPrimaFacieDecisionPayload,
    PrimaFacieFinalDecisionDetail,
} from './types';

export const primaFacieService = {
    getCategoryMemberDropdown: async (
        categoryId: number
    ): Promise<ApiResponse<CategoryMemberDropdownItem[]>> => {
        const response = await apiClient.get(
            `/CategoryAdministrators/GetCategoryMemberDropdown?categoryId=${categoryId}`
        );
        return response.data;
    },

    assignMembersForPrimaFacie: async (
        payload: AssignMembersForPrimaFaciePayload
    ): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post(
            '/PrimaFacies/GroupMemberAssignmentForPrimaFacie',
            payload
        );
        return response.data;
    },

    getMemberAssignedSubmissionsForPrimaFacie: async (
        payload: GetMemberAssignedSubmissionsForPrimaFaciePayload
    ): Promise<ApiResponse<PaginatedResponse<PrimaFacieAssignedSubmission>>> => {
        const response = await apiClient.post(
            '/PrimaFacies/GetMemberAssignedSubmissionsForPrimaFacie',
            {
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                sortBy: payload.sortBy || '',
                sortDescending: payload.sortDescending ?? true,
            }
        );
        return response.data;
    },

    getMemberAssignedSubmissionsForReviewerEvaluation: async (
        payload: GetMemberAssignedSubmissionsForReviewerEvaluationPayload
    ): Promise<ApiResponse<PaginatedResponse<ReviewerEvaluationAssignedSubmission>>> => {
        const response = await apiClient.post(
            '/ReviewerEvaluations/GetMemberAssignedManuscriptReviewForEvaluation',
            {
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                sortBy: payload.sortBy || '',
                sortDescending: payload.sortDescending ?? true,
            }
        );
        return response.data;
    },

    getPrimaFacieAssignment: async (
        primaFacieAssignmentId: string
    ): Promise<ApiResponse<PrimaFacieAssignmentDetail>> => {
        const response = await apiClient.get(
            `/PrimaFacies/GetPrimaFacieAssignment?primaFacieAssignmentId=${primaFacieAssignmentId}`
        );
        return response.data;
    },

    createUpdatePrimaFacieReview: async (
        payload: CreateUpdatePrimaFacieReviewPayload
    ): Promise<ApiResponse<string>> => {
        const response = await apiClient.post(
            '/PrimaFacies/CreateUpdatePrimaFacieReview',
            payload
        );
        return response.data;
    },
};

export const primaFacieReportService = {
    getSubmissionsUnderPrimaFacieProcess: async (
        payload: GetSubmissionUnderPrimaFacieProcessPayload
    ): Promise<ApiResponse<PaginatedResponse<PrimaFacieProcessItem>>> => {
        const response = await apiClient.post(
            '/Submissions/GetSubmissionUnderPrimaFacieProcess',
            {
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                sortBy: payload.sortBy || '',
                sortDescending: payload.sortDescending ?? true,
                search: payload.search || '',
                completed: payload.completed,
                categoryId: payload.categoryId,
            }
        );
        return response.data;
    },
};

export const primaFacieFinalDecisionService = {
    getSuggestedReviewersBySubmissionId: async (
        submissionId: string
    ): Promise<ApiResponse<PrimaFacieSuggestedReviewerItem[]>> => {
        const response = await apiClient.get(
            `/PrimaFacies/GetPrimaFacieSuggestedReviewerBySubmissionId?SubmissionId=${submissionId}`
        );
        return response.data;
    },

    submitFinalDecision: async (
        payload: SubmitFinalPrimaFacieDecisionPayload
    ): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post(
            '/PrimaFacies/SubmitFinalPrimaFacieDecision',
            payload
        );
        return response.data;
    },

    getFinalDecisionBySubmissionId: async (
        submissionId: string
    ): Promise<ApiResponse<PrimaFacieFinalDecisionDetail>> => {
        const response = await apiClient.get(
            `/PrimaFacies/GetPrimaFacieFinalDecisionBySubmissionId?SubmissionId=${submissionId}`
        );
        return response.data;
    },

    deletePrimaFacieAssignment: async (
        primaFacieAssignmentId: string
    ): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post(
            `/PrimaFacies/DeletePrimaFaciaAssignment?primaFacieAssignmentId=${primaFacieAssignmentId}`
        );
        return response.data;
    },
};
